import type { ChurchFinanceBranch, ChurchFinanceMonthNet } from "@ibada/shared-types/overview"

import type { GlobalBranchRow } from "@/features/finance/overview/lib/use-global-branch-rows"
import { shareOfTotal } from "@/features/finance/overview/lib/use-global-branch-rows"
import type { ChurchActivityRow } from "@/features/finance/overview/lib/use-church-activity-rows"

/*
 * Presentation helpers for the F3 tiles. Money stays a string everywhere it is SHOWN; it becomes a number only
 * for bar geometry, which is the one place a length has to be computed. No total is computed here: every sum the
 * tiles state comes from the server.
 */

export const isNegativeMoney = (value: string) => value.trim().startsWith("-")

const toNumber = (value: string) => {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

/** A bar length as a fraction (0 to 1) of the largest value it is drawn against. */
export const barRatio = (value: string, max: number) => (max > 0 ? Math.min(1, Math.abs(toNumber(value)) / max) : 0)

/* ---------------------------------- Branches ---------------------------------- */

export interface BranchBarRow extends GlobalBranchRow {
  incomeRatio: number
  expenseRatio: number
}

/** Income and expense bars on ONE shared scale, so a short bar always means less money than a long one. */
export function branchBars(rows: readonly GlobalBranchRow[]): BranchBarRow[] {
  const max = Math.max(0, ...rows.flatMap((row) => [Math.abs(toNumber(row.income)), Math.abs(toNumber(row.expense))]))
  return rows.map((row) => ({ ...row, incomeRatio: barRatio(row.income, max), expenseRatio: barRatio(row.expense, max) }))
}

/* ----------------------------------- Shares ----------------------------------- */

/** How many branches the share bar names before grouping the rest as "others". */
export const SHARE_NAMED_SEGMENTS = 4

export interface ShareSegment {
  /** A branch id, or null for the grouped "others" segment. */
  id: string | null
  name: string
  /** Percent of church income, unrounded (the legend rounds). */
  percent: number
}

/**
 * Each branch's share of church income, largest first, the smallest grouped as "others".
 *
 * Disclosed, a share is the branch's income over the SERVER's church-wide income. Withheld (ADR-0024), the server
 * already sent each branch's income as its share in percent, so it is read as is: the browser never sees, or
 * rebuilds, an amount.
 */
export function shareSegments(
  rows: readonly GlobalBranchRow[],
  totalIncome: string,
  figuresAreRatios: boolean,
  othersLabel: string,
): ShareSegment[] {
  const shares = rows
    .map((row) => ({
      id: row.id,
      name: row.name,
      percent: figuresAreRatios ? Math.max(0, toNumber(row.income)) : Math.max(0, shareOfTotal(row.income, totalIncome)),
    }))
    .filter((share) => share.percent > 0)
    .sort((left, right) => right.percent - left.percent)

  if (shares.length <= SHARE_NAMED_SEGMENTS + 1) return shares
  const named = shares.slice(0, SHARE_NAMED_SEGMENTS)
  const others = shares.slice(SHARE_NAMED_SEGMENTS).reduce((sum, share) => sum + share.percent, 0)
  return [...named, { id: null, name: othersLabel, percent: others }]
}

/* --------------------------------- Struggling --------------------------------- */

export interface MonthNetBar extends ChurchFinanceMonthNet {
  /** Bar length as a fraction (0 to 1) of the largest month, either sign. */
  ratio: number
  isNegative: boolean
}

export interface StrugglingSummary {
  /** Every branch in deficit this period, worst first. */
  deficits: GlobalBranchRow[]
  worst: GlobalBranchRow | null
  /** The worst branch's figures from the church endpoint, when it is in them. */
  /** `months` is empty when the recent months carry no money at all (nothing worth drawing). */
  detail: { streak: number; months: MonthNetBar[]; isParish: boolean } | null
}

export function monthNetBars(months: readonly ChurchFinanceMonthNet[]): MonthNetBar[] {
  const max = Math.max(0, ...months.map((month) => Math.abs(toNumber(month.net))))
  return months.map((month) => ({ ...month, ratio: barRatio(month.net, max), isNegative: isNegativeMoney(month.net) }))
}

export function strugglingSummary(
  rows: readonly GlobalBranchRow[],
  branches: readonly ChurchFinanceBranch[] | null,
): StrugglingSummary {
  const deficits = rows
    .filter((row) => row.netIsNegative)
    .slice()
    .sort((left, right) => toNumber(left.net) - toNumber(right.net))
  const worst = deficits[0] ?? null
  const figures = worst ? branches?.find((branch) => branch.branchId === worst.id) : undefined
  return {
    deficits,
    worst,
    detail: figures
      ? {
          streak: figures.deficitStreak,
          months: figures.recentNet.every((month) => toNumber(month.net) === 0) ? [] : monthNetBars(figures.recentNet),
          isParish: figures.isParish,
        }
      : null,
  }
}

/* ----------------------------------- Silent ----------------------------------- */

export interface SilentBranch {
  id: string
  name: string
  isParish: boolean
  lastEntryAt: string | null
  daysSinceLastEntry: number | null
  phoneNumber: string | null
}

/**
 * Branches with no money recorded this period, longest silent first when the dates are known.
 *
 * The overview's branch table only lists branches that posted something, so a branch that recorded nothing at all
 * is simply ABSENT from it. When the church figures are available they list every active branch, and a branch is
 * silent when it has no income or expense row this period. Without them (absolutes withheld) only the rows the
 * overview returned can be judged.
 */
export function silentBranches(
  rows: readonly GlobalBranchRow[],
  branches: readonly ChurchFinanceBranch[] | null,
  parishLabel: string,
): SilentBranch[] {
  const active = new Set(rows.filter((row) => !row.isSilent).map((row) => row.id))
  const fromRows = (): SilentBranch[] =>
    rows
      .filter((row) => row.isSilent)
      .map((row) => ({ id: row.id, name: row.name, isParish: false, lastEntryAt: null, daysSinceLastEntry: null, phoneNumber: null }))
  const silent: SilentBranch[] = branches
    ? branches
        .filter((branch) => !active.has(branch.branchId))
        .map((branch) => ({
          id: branch.branchId,
          name: branch.isParish ? parishLabel : branch.name,
          isParish: branch.isParish,
          lastEntryAt: branch.lastEntryAt,
          daysSinceLastEntry: branch.daysSinceLastEntry,
          phoneNumber: branch.phoneNumber,
        }))
    : fromRows()
  return silent.sort((left, right) => (right.daysSinceLastEntry ?? Infinity) - (left.daysSinceLastEntry ?? Infinity))
}

/* --------------------------------- Activities --------------------------------- */

export interface ActivityBar extends ChurchActivityRow {
  ratio: number
}

/** Activities that cost the church first (deepest loss first), then those that raised money (largest first). */
export function activityBars(rows: readonly ChurchActivityRow[]): ActivityBar[] {
  const costly = rows.filter((row) => row.netIsNegative).sort((left, right) => toNumber(left.net) - toNumber(right.net))
  const raising = rows
    .filter((row) => !row.netIsNegative && toNumber(row.net) !== 0)
    .sort((left, right) => toNumber(right.net) - toNumber(left.net))
  const ordered = [...costly, ...raising]
  const max = Math.max(0, ...ordered.map((row) => Math.abs(toNumber(row.net))))
  return ordered.map((row) => ({ ...row, ratio: barRatio(row.net, max) }))
}

/* -------------------------------- Remittances -------------------------------- */

/** How a branch's remittance this period compares with the previous one. */
export enum RemittanceStanding {
  /** Remitted at least as much as the previous period. */
  Steady = "steady",
  /** Remitted something, but less than the previous period. */
  Lower = "lower",
  /** Nothing remitted this period. */
  None = "none",
}

export interface RemittanceRow {
  id: string
  name: string
  remitted: string
  remittedPrevious: string
  ratio: number
  previousRatio: number
  standing: RemittanceStanding
}

/** Operational branches only (the parish does not remit to itself), on one shared scale across both periods. */
export function remittanceRows(branches: readonly ChurchFinanceBranch[]): RemittanceRow[] {
  const operational = branches.filter((branch) => !branch.isParish)
  const max = Math.max(
    0,
    ...operational.flatMap((branch) => [toNumber(branch.remitted), toNumber(branch.remittedPrevious)]),
  )
  return operational
    .map((branch) => {
      const current = toNumber(branch.remitted)
      const previous = toNumber(branch.remittedPrevious)
      const standing =
        current <= 0 ? RemittanceStanding.None : current >= previous ? RemittanceStanding.Steady : RemittanceStanding.Lower
      return {
        id: branch.branchId,
        name: branch.name,
        remitted: branch.remitted,
        remittedPrevious: branch.remittedPrevious,
        ratio: barRatio(branch.remitted, max),
        previousRatio: barRatio(branch.remittedPrevious, max),
        standing,
      }
    })
    .sort((left, right) => toNumber(right.remitted) - toNumber(left.remitted))
}

export const hasAnyRemittance = (rows: readonly RemittanceRow[]) =>
  rows.some((row) => toNumber(row.remitted) > 0 || toNumber(row.remittedPrevious) > 0)

/* ------------------------------------ Dates ------------------------------------ */

/** A `YYYY-MM` month as a short label ("nov."). */
export function monthShortLabel(month: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { month: "short" }).format(new Date(`${month}-15T12:00:00`))
}

/** An ISO instant as a short day label ("12 oct."). */
export function dayShortLabel(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { day: "numeric", month: "short" }).format(new Date(iso))
}

/* ------------------------------ Closest to deficit ------------------------------ */

export interface MarginRow {
  id: string
  name: string
  /** Net as a percentage of the branch's own income: how much of each unit received it kept. */
  marginPercent: number
}

/** How many near-deficit branches the struggling tile names when nobody is in deficit. */
export const CLOSEST_TO_DEFICIT_LIMIT = 2

/**
 * When every branch covers its costs, the ones with the thinnest margin are the ones to watch: lowest net over
 * income first. Only branches that earned something can have a margin.
 */
export function closestToDeficit(rows: readonly GlobalBranchRow[], limit = CLOSEST_TO_DEFICIT_LIMIT): MarginRow[] {
  return rows
    .filter((row) => !row.netIsNegative && toNumber(row.income) > 0)
    .map((row) => ({ id: row.id, name: row.name, marginPercent: (toNumber(row.net) / toNumber(row.income)) * 100 }))
    .sort((left, right) => left.marginPercent - right.marginPercent)
    .slice(0, limit)
}

/** Whether a branch table carries any money at all (a branch with income or expenses this period). */
export const hasBranchMoney = (rows: readonly GlobalBranchRow[]) => rows.some((row) => !row.isSilent)
